import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';

/**
 * ProfileSummary
 * 
 * Signature interaction:
 * - Triggered once when user scrolls downward into the summary section (IntersectionObserver).
 * - Asymmetric, architectural frame around the profile photo with 6 fragments on desktop,
 *   4 simplified corner fragments on mobile.
 * - Neutral lines (stone-700) with muted yellow accent (yellow-600) used sparingly.
 * - Peak opacity during assembly ~65%, settles into static ~45% opacity for clear definition.
 * - Desktop pointer response: frame lines subtly float (±5px) on mousemove; photo stays completely stable.
 * - Assembly duration ~900ms.
 * - Coordinated entrance: frame assembles first -> byline/eyebrow -> heading ->
 *   paragraphs stagger -> photography CTA arrow/underline reveals.
 * - Zero layout shift, aria-hidden and pointer-events: none on geometry.
 * - Respects prefers-reduced-motion.
 */
export default function ProfileSummary() {
  const sectionRef = useRef(null);
  const [frameTriggered, setFrameTriggered] = useState(false);
  const [prefersReduced, setPrefersReduced] = useState(false);
  const [frameReady, setFrameReady] = useState(false);

  // Desktop pointer response for the frame lines only
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 100, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 100, damping: 20 });

  const handleMouseMove = (e) => {
    if (prefersReduced || !frameReady || window.innerWidth < 1024) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    mouseX.set((x / (rect.width / 2)) * 5);
    mouseY.set((y / (rect.height / 2)) * 5);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mq.matches);
    const onChange = (e) => setPrefersReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !frameTriggered) {
          setFrameTriggered(true);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [frameTriggered]);

  useEffect(() => {
    if (!frameTriggered) return;
    if (prefersReduced) {
      setFrameReady(true);
      return;
    }
    const timer = window.setTimeout(() => setFrameReady(true), 1050);
    return () => window.clearTimeout(timer);
  }, [frameTriggered, prefersReduced]);

  // Timing configuration
  const duration = prefersReduced ? 0 : 0.9;
  const easeCurve = [0.22, 1, 0.36, 1];

  // SVG fragment animation variants
  const createFragmentVariants = (initialOffset, delay = 0) => ({
    hidden: {
      opacity: 0,
      x: initialOffset.x,
      y: initialOffset.y,
      pathLength: 0,
    },
    visible: {
      opacity: prefersReduced ? 0.74 : [0, 0.9, 0.74],
      x: 0,
      y: 0,
      pathLength: 1,
      transition: {
        duration: duration,
        delay: prefersReduced ? 0 : delay,
        ease: easeCurve,
        times: prefersReduced ? undefined : [0, 0.55, 1],
      },
    },
  });

  return (
    <section
      id="section-summary"
      ref={sectionRef}
      className="snap-start min-h-screen bg-stone-50 w-full px-4 sm:px-5 py-10 sm:py-12 md:p-20 flex items-center"
    >
      <div className="max-w-6xl mx-auto w-full pt-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-7 md:gap-12 lg:gap-16 items-center">

          {/* LEFT: Photo + Architectural Geometry Frame */}
          <div className="md:col-span-5 lg:col-span-4 flex justify-center md:justify-start">
            <div 
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              className="relative w-40 h-40 min-[375px]:w-44 min-[375px]:h-44 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-72 lg:h-72 flex-shrink-0"
            >

              {/* Architectural Geometry Frame — aria-hidden, pointer-events:none, z-0, responsive parallax */}
              <motion.svg
                aria-hidden="true"
                focusable="false"
                className="absolute inset-0 w-full h-full"
                style={{ x: springX, y: springY, pointerEvents: 'none', zIndex: 0 }}
                viewBox="0 0 280 280"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* --- DESKTOP FRAGMENTS (6 intentional, asymmetric strokes) --- */}
                {/* 1. Top-Left Main Corner (Neutral) */}
                <motion.path
                  d="M 8 70 L 8 8 L 130 8"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="square"
                  className="text-stone-700 hidden lg:block"
                  variants={createFragmentVariants({ x: -14, y: -10 }, 0.05)}
                  initial="hidden"
                  animate={frameTriggered ? 'visible' : 'hidden'}
                />

                {/* 2. Top Accent Segment (Muted Yellow) */}
                <motion.path
                  d="M 175 8 L 225 8"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="square"
                  className="text-yellow-600 hidden lg:block"
                  variants={createFragmentVariants({ x: 0, y: -12 }, 0.12)}
                  initial="hidden"
                  animate={frameTriggered ? 'visible' : 'hidden'}
                />

                {/* 3. Top-Right Asymmetric Corner (Neutral) */}
                <motion.path
                  d="M 245 8 L 272 8 L 272 95"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="square"
                  className="text-stone-700 hidden lg:block"
                  variants={createFragmentVariants({ x: 12, y: -10 }, 0.18)}
                  initial="hidden"
                  animate={frameTriggered ? 'visible' : 'hidden'}
                />

                {/* 4. Right Edge Guide & Bottom Corner (Neutral) */}
                <motion.path
                  d="M 272 165 L 272 272 L 170 272"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="square"
                  className="text-stone-700 hidden lg:block"
                  variants={createFragmentVariants({ x: 12, y: 12 }, 0.22)}
                  initial="hidden"
                  animate={frameTriggered ? 'visible' : 'hidden'}
                />

                {/* 5. Bottom Accent Segment (Muted Yellow) */}
                <motion.path
                  d="M 140 272 L 105 272"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="square"
                  className="text-yellow-600 hidden lg:block"
                  variants={createFragmentVariants({ x: -6, y: 12 }, 0.28)}
                  initial="hidden"
                  animate={frameTriggered ? 'visible' : 'hidden'}
                />

                {/* 6. Bottom-Left Corner & Vertical Guide (Neutral) */}
                <motion.path
                  d="M 8 195 L 8 272 L 80 272"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="square"
                  className="text-stone-700 hidden lg:block"
                  variants={createFragmentVariants({ x: -12, y: 12 }, 0.15)}
                  initial="hidden"
                  animate={frameTriggered ? 'visible' : 'hidden'}
                />

                {/* --- MOBILE FRAGMENTS (Simpler 4 corner markers) --- */}
                {/* Mobile Top-Left */}
                <motion.path
                  d="M 8 36 L 8 8 L 36 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                  className="text-stone-700 lg:hidden"
                  variants={createFragmentVariants({ x: -10, y: -8 }, 0.05)}
                  initial="hidden"
                  animate={frameTriggered ? 'visible' : 'hidden'}
                />

                {/* Mobile Top-Right (with yellow accent) */}
                <motion.path
                  d="M 244 8 L 272 8 L 272 36"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                  className="text-yellow-600 lg:hidden"
                  variants={createFragmentVariants({ x: 10, y: -8 }, 0.12)}
                  initial="hidden"
                  animate={frameTriggered ? 'visible' : 'hidden'}
                />

                {/* Mobile Bottom-Right */}
                <motion.path
                  d="M 272 244 L 272 272 L 244 272"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                  className="text-stone-700 lg:hidden"
                  variants={createFragmentVariants({ x: 10, y: 8 }, 0.18)}
                  initial="hidden"
                  animate={frameTriggered ? 'visible' : 'hidden'}
                />

                {/* Mobile Bottom-Left */}
                <motion.path
                  d="M 36 272 L 8 272 L 8 244"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                  className="text-stone-700 lg:hidden"
                  variants={createFragmentVariants({ x: -10, y: 8 }, 0.24)}
                  initial="hidden"
                  animate={frameTriggered ? 'visible' : 'hidden'}
                />
                {/* --- Precision Crosshair Markers (+) --- */}
                <g stroke="currentColor" strokeWidth="1" className="text-stone-400">
                  {/* Top-Left Crosshair */}
                  <path d="M 4 8 H 12 M 8 4 V 12" />
                  {/* Top-Right Crosshair */}
                  <path d="M 268 8 H 276 M 272 4 V 12" />
                  {/* Bottom-Left Crosshair */}
                  <path d="M 4 272 H 12 M 8 268 V 276" />
                  {/* Bottom-Right Crosshair */}
                  <path d="M 268 272 H 276 M 272 268 V 276" />
                </g>
              </motion.svg>

              {/* Profile portrait — z-10 above geometry, inset 18px so frame breathes around it */}
              <img
                src="/profile.jpg"
                alt="Ketut Ivan Sridana"
                className="absolute z-10 object-cover rounded-sm shadow-sm"
                style={{ inset: '18px' }}
              />
            </div>
          </div>

          {/* RIGHT: Content Narrative with Coordinated Entrance */}
          <div className="md:col-span-7 lg:col-span-8 flex flex-col justify-center">
            
            {/* 1. Eyebrow / Editorial Byline */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={frameTriggered ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
              transition={{
                duration: prefersReduced ? 0 : 0.6,
                delay: prefersReduced ? 0 : 0.2,
                ease: 'easeOut',
              }}
              className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-stone-500 mb-3 md:mb-4 flex flex-wrap items-center gap-x-2.5 gap-y-1"
            >
              <span className="text-stone-900 font-semibold tracking-[0.25em]">
                Ketut Ivan Sridana
              </span>
              <span className="text-stone-300 font-light select-none" aria-hidden="true">
                /
              </span>
              <span>
                Cybersecurity GRC · EY Indonesia
              </span>
            </motion.div>

            {/* 2. Large Heading (enters shortly after frame with 10-14px upward reveal) */}
            <motion.h2
              initial={{ opacity: 0, y: 12, clipPath: 'inset(0 0 100% 0)' }}
              animate={frameTriggered ? { opacity: 1, y: 0, clipPath: 'inset(0 0 0% 0)' } : { opacity: 0, y: 12, clipPath: 'inset(0 0 100% 0)' }}
              transition={{
                duration: prefersReduced ? 0 : 0.7,
                delay: prefersReduced ? 0 : 0.38,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="text-[23px] min-[375px]:text-[26px] sm:text-[32px] md:text-[44px] lg:text-[48px] xl:text-[50px] font-serif text-stone-900 leading-[1.15] tracking-tight mb-4 sm:mb-5 md:mb-8 max-w-xl lg:max-w-2xl"
            >
              Turning complex requirements into practical action.
            </motion.h2>

            {/* 3. Body Copy (3 paragraphs with restrained stagger) */}
            <div className="space-y-3 sm:space-y-3.5 md:space-y-5 text-stone-600 font-light text-[13.5px] min-[375px]:text-[14px] sm:text-[15px] md:text-[16px] leading-[1.7] md:leading-[1.75] max-w-2xl">
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={frameTriggered ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{
                  duration: prefersReduced ? 0 : 0.6,
                  delay: prefersReduced ? 0 : 0.56,
                  ease: 'easeOut',
                }}
              >
                I work across financial services, telecommunications, and industrial enterprises, helping organisations translate security, risk, and compliance requirements into practical ways of working.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={frameTriggered ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{
                  duration: prefersReduced ? 0 : 0.6,
                  delay: prefersReduced ? 0 : 0.7,
                  ease: 'easeOut',
                }}
              >
                With a Computer Engineering background, my work spans cybersecurity governance and maturity assessments, controls testing, risk assessments, and reviews of IT operations and service management. I am particularly interested in how evidence, process, and technology come together to make controls useful in day-to-day decisions.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={frameTriggered ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{
                  duration: prefersReduced ? 0 : 0.6,
                  delay: prefersReduced ? 0 : 0.84,
                  ease: 'easeOut',
                }}
              >
                Outside consulting, I photograph people, places, and ordinary moments worth noticing.
              </motion.p>
            </div>

            {/* 4. Photography CTA (reveals after copy is visible, placed directly below last paragraph) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={frameTriggered ? { opacity: 1 } : { opacity: 0 }}
              transition={{
                duration: prefersReduced ? 0 : 0.6,
                delay: prefersReduced ? 0 : 1.02,
                ease: 'easeOut',
              }}
              className="mt-7 md:mt-9"
            >
              <Link
                to="/photography"
                className="group inline-flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-4"
                aria-label="Explore my photography site"
              >
                <span className="border-b border-stone-300 group-hover:border-stone-900 transition-colors duration-300 pb-[2px]">
                  Explore my photography
                </span>
                <motion.span
                  initial={{ opacity: 0, x: -6 }}
                  animate={frameTriggered ? { opacity: 1, x: 0 } : { opacity: 0, x: -6 }}
                  transition={{
                    duration: prefersReduced ? 0 : 0.45,
                    delay: prefersReduced ? 0 : 1.15,
                    ease: 'easeOut',
                  }}
                  className="inline-block transition-transform duration-300 group-hover:translate-x-1.5"
                  aria-hidden="true"
                >
                  →
                </motion.span>
              </Link>
            </motion.div>

          </div>
        </div>
      </div>
    </section>
  );
}
